
import React from 'react';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer } from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';

interface ProjectDistributionChartProps {
  projectHoursData: { name: string; value: number; id: string }[];
  hasProjectData: boolean;
}

export function ProjectDistributionChart({ projectHoursData, hasProjectData }: ProjectDistributionChartProps) {
  const isMobile = useIsMobile();
  
  // Pie chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#83a6ed', '#8dd1e1'];

  // Custom tooltip component for charts
  const renderCustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="font-bold">{payload[0].payload.name}</div>
        <div>{parseFloat(payload[0].value).toFixed(1)} hours</div>
      </div>
    );
  };

  // Simplified label for mobile
  const renderCustomLabel = ({ name, value, percent }: any) => {
    if (isMobile) {
      // Don't show labels on mobile for cleaner appearance
      return null;
    }
    
    // Only show label if percent is significant enough
    return percent > 0.05 ? `${name}: ${Number(value).toFixed(1)}h` : null;
  };

  return (
    <Card className={isMobile ? "border-0 shadow-sm rounded-lg" : ""}>
      <CardHeader className={isMobile ? "p-3 pb-2" : ""}>
        <CardTitle className={isMobile ? "text-lg" : ""}>Project Distribution</CardTitle>
        <CardDescription className={isMobile ? "text-xs" : ""}>Hours logged by project</CardDescription>
      </CardHeader>
      <CardContent className={`${isMobile ? "p-2" : ""} h-[300px]`}>
        {hasProjectData ? (
          <ChartContainer
            config={
              projectHoursData.reduce((acc, item, index) => {
                acc[item.id] = {
                  label: item.name,
                  color: COLORS[index % COLORS.length],
                };
                return acc;
              }, {} as Record<string, { label: string; color: string }>)
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={projectHoursData}
                  cx="50%"
                  cy="50%"
                  labelLine={!isMobile}
                  outerRadius={isMobile ? 70 : 80}
                  innerRadius={isMobile ? 30 : 0}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={renderCustomLabel}
                  paddingAngle={isMobile ? 2 : 0}
                >
                  {projectHoursData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={renderCustomTooltip} />
                <Legend 
                  layout={isMobile ? "horizontal" : "vertical"} 
                  verticalAlign={isMobile ? "bottom" : "middle"}
                  align={isMobile ? "center" : "right"}
                  wrapperStyle={isMobile ? { fontSize: '10px', paddingTop: '10px' } : {}}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            No project data available
          </div>
        )}
      </CardContent>
    </Card>
  );
}
